import { useTranslation } from 'react-i18next'
import { mdiAccountSwitchOutline } from '@mdi/js'
import { useAuth } from '../../contexts/AuthContext'
import { impersonatedBy } from '../../lib/impersonation'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'

/**
 * The persistent banner of an impersonated session (spec 2026-10-04-03,
 * patterns §34): on every page while the access token carries
 * `impersonated_by`, not dismissable. « End session » logs out, which ends
 * the impersonated session on the server too.
 */
export function ImpersonationBanner() {
    const { accessToken, isAuthenticated, user, orgCode, logout } = useAuth()

    if (!isAuthenticated || !impersonatedBy(accessToken)) return null
    return (
        <ImpersonationBannerView
            name={user?.name || user?.email || ''}
            onEndSession={() => { void logout(orgCode ?? undefined) }}
        />
    )
}

/** The banner itself, without the session: also the patterns page demo. */
export function ImpersonationBannerView({ name, onEndSession }: { name: string; onEndSession: () => void }) {
    const { t } = useTranslation('admin')
    return (
        <div
            role="status"
            className="w-full bg-warning/15 border-b border-warning/40 text-foreground px-4 py-2 flex items-center gap-3 text-sm"
            data-testid="impersonation-banner"
        >
            <AppIcon path={mdiAccountSwitchOutline} size="sm" className="text-warning shrink-0" />
            <span className="min-w-0 truncate" data-testid="impersonation-banner-text">
                {t('impersonation.banner', { name })}
            </span>
            <Button
                variant="outline"
                size="sm"
                className="ml-auto h-7 shrink-0"
                onClick={onEndSession}
                data-testid="impersonation-end-session"
            >
                {t('impersonation.end_session')}
            </Button>
        </div>
    )
}
